import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import apiClient from "../../../utils/apiClient";
import { hasPermission, PERMISSIONS } from "../../../utils/permissions";
import Loader from "../../Loader";

const ListSchools = () => {
  const { user } = useSelector((state) => state.auth || {});
  const canEdit = hasPermission(user, PERMISSIONS.SCHOOLS_EDIT);
  const canDelete = hasPermission(user, PERMISSIONS.SCHOOLS_DELETE);

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionId, setActionId] = useState("");

  const loadSchools = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await apiClient.get("/admin/schools");
      setSchools(Array.isArray(data?.schools) ? data.schools : []);
    } catch (err) {
      const message =
        err?.response?.data?.errMessage ||
        err?.response?.data?.message ||
        "Failed to load registered schools.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  const filteredSchools = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return schools;

    return schools.filter((school) => {
      const name = (school?.name || "").toLowerCase();
      const email = (school?.email || "").toLowerCase();
      const contact = (school?.contactPerson || "").toLowerCase();
      const phone = (school?.phone || "").toLowerCase();

      return (
        name.includes(query) ||
        email.includes(query) ||
        contact.includes(query) ||
        phone.includes(query)
      );
    });
  }, [schools, searchTerm]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  const updateStatus = async (schoolId, accountStatus) => {
    setActionId(schoolId);
    setError("");

    try {
      await apiClient.put(`/admin/schools/${schoolId}/status`, {
        accountStatus,
        verify: accountStatus === "Active",
      });
      await loadSchools();
    } catch (err) {
      const message =
        err?.response?.data?.errMessage ||
        err?.response?.data?.message ||
        "Failed to update school status.";
      setError(message);
    } finally {
      setActionId("");
    }
  };

  const handleActivate = (school) => {
    const message = school.isVerified
      ? `Activate ${school.name}?`
      : `Activate and verify ${school.name}? The school will be able to log in.`;

    if (!window.confirm(message)) return;
    updateStatus(school._id, "Active");
  };

  const handleDeactivate = (school) => {
    if (!window.confirm(`Deactivate ${school.name}?`)) return;
    updateStatus(school._id, "InActive");
  };

  const handleBlock = (school) => {
    if (!window.confirm(`Block ${school.name}? They will not be able to log in.`)) return;
    updateStatus(school._id, "Blocked");
  };

  const handleDelete = async (school) => {
    if (
      !window.confirm(
        `Delete ${school.name}? This removes the school and its payment records permanently.`
      )
    ) {
      return;
    }

    setActionId(school._id);
    setError("");

    try {
      await apiClient.delete(`/admin/schools/${school._id}`);
      await loadSchools();
    } catch (err) {
      const message =
        err?.response?.data?.errMessage ||
        err?.response?.data?.message ||
        "Failed to delete school.";
      setError(message);
    } finally {
      setActionId("");
    }
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            School Portal
            <small>Registered Schools</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">School Portal</li>
          </ol>
        </section>

        <section className="content">
          <div className="row">
            <div className="col-xs-12">
              <div className="box">
                <div className="box-header">
                  <h3 className="box-title">Registered Schools</h3>
                  <div className="box-tools">
                    <div
                      className="input-group input-group-sm"
                      style={{ width: "220px" }}
                    >
                      <input
                        type="text"
                        className="form-control pull-right"
                        placeholder="Search schools"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <div className="input-group-btn">
                        <button type="button" className="btn btn-default">
                          <i className="fa fa-search"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="box-body table-responsive no-padding">
                  {error ? (
                    <div className="alert alert-danger" style={{ margin: "15px" }}>
                      {error}
                    </div>
                  ) : null}

                  {loading ? (
                    <div style={{ padding: "20px", textAlign: "center" }}>
                      <Loader />
                    </div>
                  ) : (
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>School Name</th>
                          <th>Email</th>
                          <th>Contact Person</th>
                          <th>Phone</th>
                          <th>Verified</th>
                          <th>Status</th>
                          <th>Batches</th>
                          <th>Registered</th>
                          {(canEdit || canDelete) && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSchools.length === 0 ? (
                          <tr>
                            <td colSpan={canEdit || canDelete ? 9 : 8} className="text-center">
                              No registered schools found.
                            </td>
                          </tr>
                        ) : (
                          filteredSchools.map((school) => {
                            const isBusy = actionId === school._id;
                            const isActive = school.accountStatus === "Active";

                            return (
                              <tr key={school._id}>
                                <td>{school.name}</td>
                                <td>{school.email}</td>
                                <td>{school.contactPerson || "-"}</td>
                                <td>{school.phone || "-"}</td>
                                <td>
                                  <span
                                    className={`label label-${
                                      school.isVerified ? "success" : "warning"
                                    }`}
                                  >
                                    {school.isVerified ? "Yes" : "Pending"}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`label label-${
                                      school.accountStatus === "Active"
                                        ? "success"
                                        : school.accountStatus === "Blocked" ||
                                            school.accountStatus === "Suspended"
                                          ? "danger"
                                          : "default"
                                    }`}
                                  >
                                    {school.accountStatus || "InActive"}
                                  </span>
                                </td>
                                <td>{school.batchesCount ?? 0}</td>
                                <td>{formatDate(school.createdAt)}</td>
                                {(canEdit || canDelete) && (
                                  <td style={{ whiteSpace: "nowrap" }}>
                                    {canEdit && !isActive && (
                                      <button
                                        type="button"
                                        className="btn btn-success btn-xs"
                                        style={{ marginRight: "4px" }}
                                        disabled={isBusy}
                                        onClick={() => handleActivate(school)}
                                      >
                                        Activate
                                      </button>
                                    )}
                                    {canEdit && isActive && (
                                      <button
                                        type="button"
                                        className="btn btn-warning btn-xs"
                                        style={{ marginRight: "4px" }}
                                        disabled={isBusy}
                                        onClick={() => handleDeactivate(school)}
                                      >
                                        Deactivate
                                      </button>
                                    )}
                                    {canEdit &&
                                      school.accountStatus !== "Blocked" &&
                                      school.accountStatus !== "Suspended" && (
                                        <button
                                          type="button"
                                          className="btn btn-default btn-xs"
                                          style={{ marginRight: "4px" }}
                                          disabled={isBusy}
                                          onClick={() => handleBlock(school)}
                                        >
                                          Block
                                        </button>
                                      )}
                                    {canDelete && (
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-xs"
                                        disabled={isBusy}
                                        onClick={() => handleDelete(school)}
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default ListSchools;
